import { MAX_QTY, currencySymbol, formatMoney, toMinor, type Currency, type Estimate, type Store } from '@sp/shared'

const NBSP = ' '

/**
 * Cena z formulare. Lide pisou "4,99", obcas "4.99" nebo "10".
 * Vraci minoritni jednotky, null pro prazdne pole, 'invalid' pro nesmysl.
 */
export function parsePriceInput(raw: string): number | null | 'invalid' {
  const text = raw.trim().replace(/\s/g, '').replace(',', '.')
  if (text === '') return null
  // Nejvys 999 999,99 — vic je preklep a databaze by takovou cenu odmitla.
  if (!/^\d{1,6}(\.\d{1,2})?$/.test(text)) return 'invalid'
  return toMinor(Number(text))
}

export function priceInputValue(minor: number | null): string {
  return minor === null ? '' : (minor / 100).toFixed(2).replace('.', ',')
}

/** Mnozstvi z formulare: "1", "0,5". Vraci null pro nesmysl. */
export function parseQtyInput(raw: string): number | null {
  const n = Number(raw.trim().replace(',', '.'))
  return Number.isFinite(n) && n > 0 && n <= MAX_QTY ? Math.round(n * 1000) / 1000 : null
}

export function formatQty(qty: number, unit: string | null): string {
  const n = qty.toLocaleString('cs-CZ', { maximumFractionDigits: 3 })
  return `${n}${NBSP}${unit ?? 'ks'}`
}

/** "3,49 zł / ks" */
export function unitPrice(minor: number | null, currency: Currency | null, unit: string | null): string | null {
  if (minor === null || currency === null) return null
  return `${formatMoney(minor, currency)} / ${unit ?? 'ks'}`
}

interface TripNaming {
  kind: 'big' | 'quick'
  title: string | null
  plannedDate: string | null
  createdAt: string
}

/**
 * Nazev nakupu pro lidi (navrh v3, jedno pravidlo pro velky i bezny nakup):
 * vlastni nazev; jinak obchod, kdyz je jeden; „Nákup kdekoli" bez obchodu;
 * s vic obchody den nakupu. date = kratke datum, kdyz uz neni v nazvu.
 */
export function tripName(
  trip: TripNaming & { storeIds: readonly string[] },
  stores: readonly { id: string; name: string }[],
): { title: string; date: string | null } {
  const day = trip.plannedDate ?? null
  const shortDay = formatShortWeekday(day ?? trip.createdAt)
  const custom = trip.title?.trim()
  if (custom) return { title: custom, date: day ? shortDay : null }
  if (trip.storeIds.length === 1) {
    const store = stores.find((s) => s.id === trip.storeIds[0])
    return { title: store?.name ?? 'Nákup', date: shortDay }
  }
  if (trip.storeIds.length === 0) return { title: 'Nákup kdekoli', date: shortDay }
  return day ? { title: capitalize(formatTripDate(day)), date: null } : { title: 'Velký nákup', date: shortDay }
}

/**
 * Podtitulek nakupu v seznamech: den (kdyz neni v nazvu), obchody (kdyz nejsou
 * v nazvu) a pocet polozek.
 */
export function tripSub(
  trip: TripNaming & { storeIds: readonly string[]; itemCount?: number | undefined },
  stores: readonly { id: string; name: string }[],
): string {
  const name = tripName(trip, stores)
  const route = trip.storeIds.flatMap((id) => stores.find((s) => s.id === id)?.name ?? [])
  const where = route.length > 1 || (route.length === 1 && trip.title?.trim()) ? route.map(storeShortName).join(' → ') : null
  return [name.date, where, trip.itemCount === undefined ? null : itemsCount(trip.itemCount)].filter(Boolean).join(' · ')
}

function localDate(isoDate: string): Date {
  // Samotne datum se parsuje na poledne mistniho casu, jinak by UTC pulnoc
  // v nekterych pasmech skocila na predchozi den.
  return new Date(isoDate.length === 10 ? `${isoDate}T12:00:00` : isoDate)
}

/** "sobota 10. 10.", s rokem jen mimo letosek. */
export function formatTripDate(isoDate: string): string {
  const d = localDate(isoDate)
  const weekday = d.toLocaleDateString('cs-CZ', { weekday: 'long' })
  const year = d.getFullYear() === new Date().getFullYear() ? '' : ` ${d.getFullYear()}`
  return `${weekday} ${d.getDate()}.${NBSP}${d.getMonth() + 1}.${year}`
}

/** "14. 6. 2026" */
export function formatDay(iso: string): string {
  const d = localDate(iso)
  return `${d.getDate()}.${NBSP}${d.getMonth() + 1}.${NBSP}${d.getFullYear()}`
}

/** „so 19. 9." */
export function formatShortWeekday(iso: string): string {
  const d = localDate(iso)
  return `${d.toLocaleDateString('cs-CZ', { weekday: 'short' })} ${d.getDate()}.${NBSP}${d.getMonth() + 1}.`
}

/** "14. 6." */
export function formatShortDay(iso: string): string {
  const d = localDate(iso)
  return `${d.getDate()}.${NBSP}${d.getMonth() + 1}.`
}

/** Ceske tvary: 1 polozka, 2 polozky, 5 polozek. */
export function plural(n: number, one: string, few: string, many: string): string {
  const word = n === 1 ? one : n >= 2 && n <= 4 ? few : many
  return `${n.toLocaleString('cs-CZ')}${NBSP}${word}`
}

export function itemsCount(n: number): string {
  return plural(n, 'položka', 'položky', 'položek')
}

const DAY_MS = 24 * 60 * 60 * 1000

/** "dnes", "včera", "před 3 dny", "před týdnem", "před 5 týdny", "před 4 měsíci". */
export function relativeAgo(iso: string, now = new Date()): string {
  const days = Math.floor((now.getTime() - new Date(iso).getTime()) / DAY_MS)
  if (days <= 0) return 'dnes'
  if (days === 1) return 'včera'
  if (days < 7) return `před ${days}${NBSP}dny`
  const weeks = Math.floor(days / 7)
  if (weeks === 1) return 'před týdnem'
  if (weeks < 17) return `před ${weeks}${NBSP}týdny`
  const months = Math.floor(days / 30)
  return months === 1 ? 'před měsícem' : `před ${months}${NBSP}měsíci`
}

/** „každý týden", „každé 3 týdny", „každých 5 týdnů"; pod dva týdny ve dnech. */
export function everyText(days: number): string {
  const weeks = days >= 14
  const n = weeks ? Math.round(days / 7) : Math.max(1, Math.round(days))
  if (n === 1) return weeks ? 'každý týden' : 'každý den'
  const few = n >= 2 && n <= 4
  const word = weeks ? (few ? 'týdny' : 'týdnů') : few ? 'dny' : 'dní'
  return `${few ? 'každé' : 'každých'} ${n}${NBSP}${word}`
}

/** Kolik celych dni jeste zbyva do casu `iso` (aspon 0). */
export function daysLeft(iso: string, now = new Date()): number {
  return Math.max(0, Math.ceil((new Date(iso).getTime() - now.getTime()) / DAY_MS))
}

export function validFor(iso: string): string {
  const days = daysLeft(iso)
  if (days <= 1) return 'Platí do zítřka'
  return `Platí ještě ${plural(days, 'den', 'dny', 'dní')}`
}

/** "Richard", "Richard a Sestra", "Richard, Sestra a Máma". */
export function joinNames(names: readonly string[]): string {
  if (names.length <= 1) return names[0] ?? ''
  return `${names.slice(0, -1).join(', ')} a ${names[names.length - 1]}`
}

/** Zlaty radek do paticky: "46,20 zł · 62,80 € · ≈ 1 840 Kč". */
export function estimateText(estimate: Estimate, currencies: readonly Currency[]): string | null {
  const order = [...new Set([...currencies, 'PLN', 'EUR', 'CZK'] as Currency[])]
  const parts = order.flatMap((c) => {
    const minor = estimate.byCurrency[c]
    return minor === undefined ? [] : [formatMoney(minor, c)]
  })
  if (parts.length === 0) return null
  const onlyCzk = parts.length === 1 && estimate.byCurrency.CZK !== undefined
  if (estimate.czkMinor !== null && !onlyCzk) parts.push(`≈${NBSP}${formatCzkRounded(estimate.czkMinor)}`)
  return parts.join(' · ')
}

export function formatCzkRounded(minor: number): string {
  return new Intl.NumberFormat('cs-CZ', {
    style: 'currency',
    currency: 'CZK',
    maximumFractionDigits: 0,
    minimumFractionDigits: 0,
  }).format(Math.round(minor / 100))
}

/** "2,49" — cena bez meny, kdyz mena uz v textu je („2,79 zł místo 2,49"). */
export function formatAmount(minor: number): string {
  return (minor / 100).toLocaleString('cs-CZ', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

export function capitalize(text: string): string {
  return text.charAt(0).toLocaleUpperCase('cs-CZ') + text.slice(1)
}

/** Hledani bez ohledu na diakritiku a velikost pismen: „maslo" najde „Máslo". */
export function searchKey(text: string): string {
  return text.normalize('NFD').replace(/\p{M}/gu, '').toLocaleLowerCase('cs-CZ').trim()
}

/** „Zittau · €" — mesto a mena pod nazvem obchodu. */
export function storeSub(store: Store): string {
  return [store.city, currencySymbol(store.currency)].filter(Boolean).join(' · ')
}

/** "Kaufland Zittau" -> "Kaufland". Do uzkeho vyberu obchodu. */
export function storeShortName(name: string): string {
  return name.split(/\s+/)[0] ?? name
}

/**
 * „Přesunout do Kauflandu". Genitiv z libovolneho nazvu obchodu neni
 * spolehlive odvoditelny, takze jen znamy retezce; jinak se napise cely nazev.
 */
const GENITIVE: Record<string, string> = {
  kaufland: 'Kauflandu',
  biedronka: 'Biedronky',
  netto: 'Netta',
  lidl: 'Lidlu',
  albert: 'Alberta',
  billa: 'Billy',
  tesco: 'Tesca',
  globus: 'Globusu',
  penny: 'Penny',
  rossmann: 'Rossmannu',
  action: 'Actionu',
  aldi: 'Aldi',
  edeka: 'Edeky',
  rewe: 'Rewe',
  norma: 'Normy',
}

export function intoStore(name: string): string {
  const short = storeShortName(name)
  const genitive = GENITIVE[short.toLowerCase()]
  return genitive ? `do ${genitive}` : `do obchodu ${name}`
}

/** „Levněji v Kauflandu" — lokal zname retezce, jinak „v obchodě Pekárna u Nováků". */
const LOCATIVE: Record<string, string> = {
  kaufland: 'Kauflandu',
  biedronka: 'Biedronce',
  netto: 'Nettu',
  lidl: 'Lidlu',
  albert: 'Albertu',
  billa: 'Bille',
  tesco: 'Tescu',
  globus: 'Globusu',
  penny: 'Penny',
  rossmann: 'Rossmannu',
  action: 'Actionu',
  aldi: 'Aldi',
  edeka: 'Edece',
  rewe: 'Rewe',
  norma: 'Normě',
  dino: 'Dinu',
  carrefour: 'Carrefouru',
}

export function inStore(name: string): string {
  const short = storeShortName(name)
  const locative = LOCATIVE[short.toLowerCase()]
  return locative ? `v ${locative}` : `v obchodě ${name}`
}

/** "Německo a Polsko" -> "NP", "Máma" -> "M". */
export function groupInitials(name: string): string {
  const words = name
    .split(/\s+/)
    .filter((w) => w.length > 1 || /\p{Lu}/u.test(w))
    .map((w) => w[0]?.toLocaleUpperCase('cs-CZ') ?? '')
  return words.slice(0, 2).join('') || '?'
}

export interface ParsedEntry {
  name: string
  qty: number
  unit: string | null
}

const UNITS = new Set(['ks', 'kg', 'g', 'dkg', 'l', 'ml', 'bal', 'bal.', 'balení'])

/**
 * "Mléko, 2 rohlíky, 500 g sýr" -> tri polozky. Cislo na zacatku je
 * mnozstvi, jen kdyz za nim je mezera — "7Up" zustane nazvem.
 * Carka pred cislici je desetinna, ne oddelovac: "Mléko 1,5 %" je jedna vec.
 */
export function splitEntries(text: string): ParsedEntry[] {
  const seen = new Set<string>()
  return text
    .split(/,(?!\d)|[;\n]/)
    .map((part) => part.trim().replace(/\s+/g, ' '))
    .filter(Boolean)
    .flatMap((part) => {
      const m = /^(\d+(?:[.,]\d+)?)\s+(\S+)(?:\s+(.+))?$/.exec(part)
      let entry: ParsedEntry = { name: part, qty: 1, unit: null }
      if (m) {
        const qty = Number((m[1] ?? '').replace(',', '.'))
        const second = m[2] ?? ''
        if (UNITS.has(second.toLowerCase()) && m[3]) {
          entry = { name: m[3], qty, unit: second.toLowerCase().startsWith('bal') ? 'bal.' : second.toLowerCase() }
        } else {
          entry = { name: m[3] ? `${second} ${m[3]}` : second, qty, unit: null }
        }
        if (!(qty > 0)) entry = { name: part, qty: 1, unit: null }
      }
      const name = entry.name.charAt(0).toLocaleUpperCase('cs-CZ') + entry.name.slice(1)
      const key = name.toLocaleLowerCase('cs-CZ')
      if (seen.has(key) || name.length > 200) return []
      seen.add(key)
      return [{ ...entry, name }]
    })
}

/** Odrazky a cislovani na zacatku radku seznamu: „- ", „• ", „[ ] ", „1. ". */
const LIST_BULLET = /^\s*(?:[-*•·–—]|\[\s?[xX✓]?\]|[☐☑✓✔]|\d{1,3}[.)](?!\d))\s*/u

/** Cena na konci radku jen s vlnovkou nebo menou, at „Rohlíky 6" nezmizi jako cena. */
const LIST_PRICE =
  /\s+(?:(?:~|≈|za)\s*(\d{1,4}(?:[.,]\d{1,2})?)\s*(?:kč|kc|zł|zl|€|eur|pln)?|(\d{1,4}(?:[.,]\d{1,2})?)\s*(?:kč|kc|zł|zl|€|eur|pln))\s*$/iu

const QTY_TIMES_BEFORE = /^(\d{1,4}(?:[.,]\d{1,3})?)\s*[x×]\s+(.+)$/iu
const QTY_TIMES_AFTER = /^(.+?)\s+[x×]?\s*(\d{1,4}(?:[.,]\d{1,3})?)\s*[x×]$/iu
/** „rohlíky 6" — cele cislo na konci za slovem je pocet kusu („Mléko 1,5 %" konci procentem). */
const QTY_TRAILING = /^(.*\p{L}.*?)\s+(\d{1,3})$/u

export interface SharedEntry extends ParsedEntry {
  /** Cena z radku („~1,29", „4,99 zł") v minoritnich jednotkach. */
  priceMinor: number | null
}

/**
 * Seznam poslany z jine appky (Messenger, poznamky): radek = polozka, odrazky
 * pryc, „2x mléko" i „mléko 2x" je mnozstvi, „~1,29" na konci je cena.
 * Uvnitr radku funguji carky a „2 kg rýže" jako v poli pro pridani.
 */
export function parseSharedList(text: string): SharedEntry[] {
  const seen = new Set<string>()
  const out: SharedEntry[] = []
  for (const rawLine of text.split(/\r?\n/)) {
    let line = rawLine.replace(LIST_BULLET, '').trim()
    // Prazdny radek, odkaz a nadpis („Nákup na sobotu:") polozky nejsou.
    if (!line || /^https?:\/\//i.test(line) || line.endsWith(':')) continue
    let priceMinor: number | null = null
    const price = LIST_PRICE.exec(line)
    if (price) {
      const parsed = parsePriceInput(price[1] ?? price[2] ?? '')
      if (typeof parsed === 'number') {
        priceMinor = parsed
        line = line.slice(0, price.index).trim()
      }
    }
    const before = QTY_TIMES_BEFORE.exec(line)
    const after = before ? null : (QTY_TIMES_AFTER.exec(line) ?? (line.includes(',') ? null : QTY_TRAILING.exec(line)))
    // „2x mléko, chléb": pocet patri jen k sousedni polozce (prvni, resp. u „…, mléko 2x" posledni).
    const entries: ParsedEntry[] = before
      ? splitEntries(before[2] ?? '').map((e, i) =>
          i === 0 ? { ...e, qty: Number((before[1] ?? '1').replace(',', '.')) } : e,
        )
      : after
        ? splitEntries(after[1] ?? '').map((e, i, all) =>
            i === all.length - 1 ? { ...e, qty: Number((after[2] ?? '1').replace(',', '.')) } : e,
          )
        : splitEntries(line)
    for (const entry of entries) {
      const key = entry.name.toLocaleLowerCase('cs-CZ')
      if (seen.has(key) || !(entry.qty > 0) || entry.qty > MAX_QTY) continue
      seen.add(key)
      // Cena patri jen k jedine polozce na radku.
      out.push({ ...entry, priceMinor: entries.length === 1 ? priceMinor : null })
    }
  }
  return out
}

/** Viceradkovy text vlozeny do jednoradkoveho pole: radky se spoji carkou, jinak null. */
export function pastedList(text: string): string | null {
  const lines = text
    .split(/\r?\n/)
    .map((line) => line.replace(LIST_BULLET, '').trim())
    .filter(Boolean)
  return lines.length > 1 ? lines.join(', ') : null
}
